import API from '@/api/apiCreate'
import { getParams } from '@/api/getParams'
import { isError } from '@/helpers/checkQuery'
import { ResponseWrapper } from '@/api/types/base.types'
import { getContractTemplateApiErrorMessage } from './contractTemplate.api.utils'
import type {
  ManualField,
  ManualFieldDeleteResponse,
  ManualFieldPreviewPayload,
  ManualFieldPreviewResult,
  ManualFieldWritePayload,
  VariableCatalogGroup,
} from './contractTemplateVariable.types'

const normalizeWritePayload = (
  payload: ManualFieldWritePayload,
): ManualFieldWritePayload => {
  const title = payload.title.trim()
  if (!title) {
    throw new Error('Введите название поля')
  }

  if (payload.type === 'number') {
    return {
      title,
      type: 'number',
      settings: {},
      ...(payload.sort != null ? { sort: payload.sort } : {}),
    }
  }

  if (payload.type !== 'calculator') {
    return {
      title,
      type: 'text',
      settings: {},
      ...(payload.sort != null ? { sort: payload.sort } : {}),
    }
  }

  const formula = String(payload.settings?.formula ?? '').trim()
  if (!formula) {
    throw new Error('Введите формулу')
  }

  const settings: ManualFieldWritePayload['settings'] = { formula }

  if (payload.settings?.precision != null) {
    const precision = Number(payload.settings.precision)
    if (!Number.isInteger(precision) || precision < 0 || precision > 10) {
      throw new Error(
        'Количество знаков после точки должно быть от 0 до 10.',
      )
    }
    settings.precision = precision
  }

  if (payload.settings?.thousands_separator != null) {
    settings.thousands_separator = Boolean(payload.settings.thousands_separator)
  }

  return {
    title,
    type: 'calculator',
    settings,
    ...(payload.sort != null ? { sort: payload.sort } : {}),
  }
}

export const contractTemplateVariableApi = {
  async getVariablesCatalog(): Promise<VariableCatalogGroup[]> {
    const res = await API.get<ResponseWrapper<VariableCatalogGroup[]>>(
      'contract-template/variables',
    )

    if (isError(res)) {
      throw new Error(
        getContractTemplateApiErrorMessage(
          res,
          'Не удалось загрузить каталог переменных',
        ),
      )
    }

    return res.data?.data ?? []
  },

  async getManualFields(): Promise<ManualField[]> {
    const res = await API.get<ResponseWrapper<ManualField[]>>(
      'contract-template-variable/index',
    )

    if (isError(res)) {
      throw new Error(
        getContractTemplateApiErrorMessage(
          res,
          'Не удалось загрузить ручные поля',
        ),
      )
    }

    return res.data?.data ?? []
  },

  async createManualField(
    payload: ManualFieldWritePayload,
  ): Promise<ManualField> {
    const body = normalizeWritePayload(payload)

    const res = await API.post<ResponseWrapper<ManualField>>(
      'contract-template-variable/create',
      body,
    )

    if (isError(res)) {
      throw new Error(
        getContractTemplateApiErrorMessage(res, 'Не удалось создать поле'),
      )
    }

    if (!res.data?.data) {
      throw new Error('Не удалось создать поле')
    }

    return res.data.data
  },

  async updateManualField(
    id: number,
    payload: ManualFieldWritePayload,
  ): Promise<ManualField> {
    const body = normalizeWritePayload(payload)

    const res = await API.post<ResponseWrapper<ManualField>>(
      `contract-template-variable/update${getParams({ id })}`,
      body,
    )

    if (isError(res)) {
      throw new Error(
        getContractTemplateApiErrorMessage(res, 'Не удалось обновить поле'),
      )
    }

    if (!res.data?.data) {
      throw new Error('Не удалось обновить поле')
    }

    return res.data.data
  },

  async deleteManualField(id: number): Promise<boolean> {
    const res = await API.delete<ResponseWrapper<ManualFieldDeleteResponse>>(
      `contract-template-variable/delete${getParams({ id })}`,
    )

    if (isError(res)) {
      throw new Error(
        getContractTemplateApiErrorMessage(res, 'Не удалось удалить поле'),
      )
    }

    return Boolean(res.data?.data?.deleted)
  },

  async previewCalculator(
    payload: ManualFieldPreviewPayload,
  ): Promise<ManualFieldPreviewResult> {
    const formula = String(payload.settings?.formula ?? '').trim()
    if (!formula) {
      throw new Error('Введите формулу')
    }

    const settings: ManualFieldPreviewPayload['settings'] = { formula }
    if (payload.settings.precision != null) {
      settings.precision = payload.settings.precision
    }
    if (payload.settings.thousands_separator != null) {
      settings.thousands_separator = payload.settings.thousands_separator
    }

    const res = await API.post<ResponseWrapper<ManualFieldPreviewResult>>(
      'contract-template-variable/preview',
      {
        type: 'calculator',
        settings,
        values: payload.values ?? {},
      },
    )

    if (isError(res)) {
      throw new Error(
        getContractTemplateApiErrorMessage(
          res,
          'Проверьте значения переменных и формулу.',
        ),
      )
    }

    if (!res.data?.data) {
      throw new Error('Проверьте значения переменных и формулу.')
    }

    return res.data.data
  },
}
